import { Component } from "react";
import {
  type DndComponentClass,
  DragSource,
  type DragSourceMonitor,
  type DragSourceSpec,
  DropTarget,
  type DropTargetMonitor,
  type DropTargetSpec,
} from "react-dnd";

import { SymbolIcon } from "widgets";

export interface IDragDropItemProps {
  dataKey: string;
  dataIndex: number;
  onMoveColumn: (dragIndex: number, hoverIndex: number) => void;
  onMouseDown?: () => void;
  canDrag?: () => boolean;
  canDrop?: () => boolean;
  innerItem?: boolean;
  connectDropTarget?: any;
  connectDragSource?: any;
}

const columnSourceSpec: DragSourceSpec<IDragDropItemProps, IDragDropItemProps> = {
  beginDrag(
    propsOfDragged: IDragDropItemProps,
    _monitor: DragSourceMonitor,
    _component?: Component<IDragDropItemProps, any>,
  ): IDragDropItemProps {
    return { ...propsOfDragged }; // to make the props object editable, so that we can modify dataIndex (see TargetSpec below)
  },
  // endDrag(props, monitor, component)
  //   called past  target.drop(), monitor.getDropResult() returns the return value of .drop()
  canDrag(propsOfDragged: IDragDropItemProps): boolean {
    // Called by monitor.canDrag():
    // monitor.getItem() returns a DragItemData
    return propsOfDragged.canDrag();
  },
};

const columnTargetSpec: DropTargetSpec<IDragDropItemProps> = {
  // hover(p.m,c) is called whenever the source moves over a target
  hover(
    propsOfTarget: IDragDropItemProps,
    monitor: DropTargetMonitor,
    _component?: Component<IDragDropItemProps, any>,
  ) {
    const dragIndex = (monitor.getItem() as IDragDropItemProps).dataIndex;
    const hoverIndex = propsOfTarget.dataIndex;

    // Don't replace items with themselves
    if (dragIndex === hoverIndex) {
      return;
    }

    if (!monitor.canDrop()) {
      return;
    }

    // Time to actually perform the action
    propsOfTarget.onMoveColumn(dragIndex, hoverIndex);

    // Note: we're mutating the monitor item here!
    // Generally it's better to avoid mutations,
    // but it's good here for the sake of performance
    // to avoid expensive index searches.
    (monitor.getItem() as IDragDropItemProps).dataIndex = hoverIndex;

    // return undefined or an object available to source.endDrop in its monitor.getDropResult() call
    // return { moved: true };
  },
  canDrop(props: IDragDropItemProps): boolean {
    // Called by monitor.canDrop()
    //  You cannot call monitor.canDrop() in here!
    return props.canDrop();
  },
};

class DragDropItem extends Component<IDragDropItemProps> {
  render() {
    const { connectDragSource, connectDropTarget, innerItem } = this.props;
    const styles: any = { position: "relative", marginLeft: innerItem ? 20 : 0 };
    const canDrag = this.props.canDrag();

    return connectDragSource(
      connectDropTarget(
        <div key={this.props.dataKey} id={this.props.dataKey} className="form-group" style={styles}>
          {canDrag && <SymbolIcon icon={"drag_indicator"} className="sortable-item" />}
          <div
            onMouseDown={this.props.onMouseDown}
            className="checkbox check-default"
            style={{ marginTop: 0, marginBottom: 0, marginLeft: 15 }}
          >
            {this.props.children}
          </div>
        </div>,
      ),
    );
  }
}

// these types are made to create a specific component from the Generic DragDrop component, so that you cannot drag/drop across dialogs
const ItemTypes = {
  TRACK: "track",
  MUTANT_GENOME: "mutant_genome",
};

export const DragDropTrackMutantGenome: DndComponentClass<any, IDragDropItemProps> = DragSource(
  ItemTypes.MUTANT_GENOME,
  columnSourceSpec,
  (connect, _monitor) => ({ connectDragSource: connect.dragSource() }),
)(
  DropTarget(ItemTypes.MUTANT_GENOME, columnTargetSpec, (connect) => ({ connectDropTarget: connect.dropTarget() }))(
    DragDropItem,
  ),
);
